/**
 * v-eb-auth：仅适用于原生 DOM 或单根 HTMLElement 组件（如 el-button）。
 * el-dropdown-item、el-dropdown、router-link、el-switch 等请用 v-if="$crmebAuth.authVisible(...)"，
 * 避免 Vue 3「Runtime directive used on component with non-element root node」警告。
 */
import type { Directive, DirectiveBinding, VNode } from 'vue';
import { nextTick } from 'vue';
import crmebAuth from '@/plugins/crmebAuth';

function isAllowed(value: string | string[] | undefined): boolean {
  if (value === undefined || value === null || value === '') return true;
  return crmebAuth.authVisible(value);
}

function removeElement(el: Element | null | undefined) {
  if (el?.parentNode) el.parentNode.removeChild(el);
}

/** Element Plus 非单根组件：解析可移除的菜单项/控件 DOM */
function resolveHostElement(el: unknown, vnode: VNode): Element | null {
  if (el instanceof Element) return el;
  const comp = vnode.component;
  if (!comp?.subTree) return null;

  const walk = (node: VNode | null): Element | null => {
    if (!node) return null;
    if (node.el instanceof Element) return node.el;
    if (node.component?.subTree) return walk(node.component.subTree);
    const children = node.children;
    if (Array.isArray(children)) {
      for (const child of children) {
        if (child && typeof child === 'object' && 'type' in (child as object)) {
          const hit = walk(child as VNode);
          if (hit) return hit;
        }
      }
    }
    return null;
  };

  const inner = walk(comp.subTree);
  if (!inner) return null;
  return inner.closest?.('[role="menuitem"]') ?? inner;
}

function apply(el: unknown, binding: DirectiveBinding<string | string[]>, vnode: VNode) {
  if (isAllowed(binding.value)) return;

  if (vnode.component) {
    const run = () => removeElement(resolveHostElement(el, vnode));
    run();
    nextTick(run);
    return;
  }

  if (el instanceof HTMLElement) {
    removeElement(el);
  }
}

const crmebAuthDirective: Directive = {
  mounted: apply,
  updated: apply,
};

export default crmebAuthDirective;
